import { useCallback, useEffect, useState } from 'react'
import { Loader2, AlertCircle, X, Check, Trash2, Save, Languages } from 'lucide-react'
import {
  listTranslations,
  upsertTranslation,
  deleteTranslation,
  CONTENT_LANGUAGES,
  CONTENT_LANGUAGE_NAMES,
  type TranslationRow,
} from '../../services/translationService'

// ================================================================================
// TRANSLATION EDITOR MODAL — shared admin UI for per-language content translation.
//
// One component serves all four translatable content types (KnowledgeArticle, Resource,
// Testimonial, Facility). The caller passes a `basePath` (the content collection, e.g.
// '/knowledge/articles', '/resources') and a `fields` list describing which superset fields
// this content type translates and how to render them. English is the canonical row — the
// backend rejects upserting "en" — so the EN tab is read-only guidance and HI/MR are editable.
// ================================================================================

export interface TranslationFieldDef {
  /** Key on the TranslationRow superset. */
  key: keyof Omit<TranslationRow, 'language'>
  label: string
  /** Single-line input vs multi-line textarea. */
  type: 'text' | 'textarea'
  /** Textarea rows (ignored for text). */
  rows?: number
  required?: boolean
  placeholder?: string
}

interface Props {
  open: boolean
  basePath: string
  id: number | null
  /** Human label for what is being translated, e.g. "resource", "article". */
  itemLabel: string
  fields: TranslationFieldDef[]
  onClose: () => void
  /** Optional canonical English values to pre-fill a new translation as a starting point. */
  canonical?: Partial<TranslationRow>
}

type DraftMap = Record<string, TranslationRow> // keyed by language code

const emptyDraft = (language: string): TranslationRow => ({ language })

export function TranslationEditorModal({
  open, basePath, id, itemLabel, fields, onClose, canonical,
}: Props) {
  const [active, setActive] = useState<string>('hi')
  const [drafts, setDrafts] = useState<DraftMap>({})
  const [present, setPresent] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [busyLang, setBusyLang] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg })
    setTimeout(() => setToast(null), 2800)
  }

  // Load all translations for this item when opened / id changes.
  const load = useCallback(async () => {
    if (!open || id == null) return
    setLoading(true); setError(null)
    try {
      const rows = await listTranslations(basePath, id)
      const map: DraftMap = {}
      const have = new Set<string>()
      for (const r of rows) {
        map[r.language] = r
        have.add(r.language)
      }
      setDrafts(map)
      setPresent(have)
      // Default to the first non-EN language that has a translation, else 'hi'.
      setActive(have.has('hi') ? 'hi' : have.has('mr') ? 'mr' : 'hi')
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load translations.')
    } finally { setLoading(false) }
  }, [open, id, basePath])

  useEffect(() => { if (open) load() }, [open, load])
  // Reset state when the modal closes so a reopen is clean.
  useEffect(() => { if (!open) { setDrafts({}); setPresent(new Set()); setError(null); setToast(null) } }, [open])

  if (!open || id == null) return null

  const draft = drafts[active] ?? emptyDraft(active)

  const setField = (key: keyof TranslationRow, value: string) => {
    const current = drafts[active] ?? emptyDraft(active)
    setDrafts({ ...drafts, [active]: { ...current, [key]: value } })
  }

  const startFromCanonical = () => {
    if (!canonical) return
    const current = drafts[active] ?? emptyDraft(active)
    const seeded: TranslationRow = { ...current }
    for (const f of fields) {
      const v = canonical[f.key]
      if (typeof v === 'string' && v) (seeded as any)[f.key] = v
    }
    setDrafts({ ...drafts, [active]: seeded })
  }

  const saveLang = async (lang: string) => {
    const d = drafts[lang]
    if (!d) { flash('error', 'Nothing to save yet — fill the fields first.'); return }
    setBusyLang(lang); setError(null)
    try {
      const body: TranslationRow = { language: lang }
      for (const f of fields) {
        const v = (d as any)[f.key]
        if (typeof v === 'string') (body as any)[f.key] = v
      }
      const saved = await upsertTranslation(basePath, id, lang, body)
      setDrafts({ ...drafts, [lang]: saved })
      setPresent((prev) => new Set(prev).add(lang))
      flash('success', `${CONTENT_LANGUAGE_NAMES[lang] || lang} translation saved.`)
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to save translation.'
      flash('error', msg)
    } finally { setBusyLang(null) }
  }

  const removeLang = async (lang: string) => {
    if (!window.confirm(`Delete the ${CONTENT_LANGUAGE_NAMES[lang] || lang} translation? The English content stays.`)) return
    setBusyLang(lang); setError(null)
    try {
      await deleteTranslation(basePath, id, lang)
      const nextDrafts = { ...drafts }
      delete nextDrafts[lang]
      setDrafts(nextDrafts)
      setPresent((prev) => { const n = new Set(prev); n.delete(lang); return n })
      flash('success', `${CONTENT_LANGUAGE_NAMES[lang] || lang} translation deleted.`)
    } catch (e: any) {
      flash('error', e?.response?.data?.message || 'Failed to delete translation.')
    } finally { setBusyLang(null) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" aria-hidden="true">
              <Languages className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Translate {itemLabel}</h3>
              <p className="text-sm text-gray-500 mt-1">
                Add Hindi / Marathi translations. English is the canonical content — edit it in the {itemLabel}&rsquo;s own form.
                Drafts are flagged for professional medical-translation review.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {error && <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {error}</div>}
        {toast && (
          <div role="status" className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        {/* Language tab strip */}
        <div className="flex border-b border-gray-200 mb-4" role="tablist" aria-label="Translation language">
          {CONTENT_LANGUAGES.map((lang) => {
            const isActive = active === lang
            const isEn = lang === 'en'
            const has = present.has(lang)
            return (
              <button
                key={lang}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(lang)}
                className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                } ${isEn ? 'cursor-default' : ''}`}
              >
                {CONTENT_LANGUAGE_NAMES[lang] || lang}
                {has && <Check className="w-3.5 h-3.5 text-green-500" aria-label="translation present" />}
                {isEn && <span className="text-[10px] text-gray-400 font-normal">(canonical)</span>}
              </button>
            )
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading translations…</div>
        ) : active === 'en' ? (
          <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
            English is the canonical source of truth for this {itemLabel}. To edit the English title, description, or
            other fields, close this dialog and use the {itemLabel}&rsquo;s edit form. The English row is never stored
            as a translation — it lives on the {itemLabel} itself.
          </div>
        ) : (
          <div className="space-y-4">
            {!present.has(active) && (
              <div className="flex items-center justify-between gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                <span>No {CONTENT_LANGUAGE_NAMES[active]} translation yet.</span>
                {canonical && (
                  <button type="button" onClick={startFromCanonical} className="text-amber-900 underline font-medium">
                    Copy English as starting point
                  </button>
                )}
              </div>
            )}
            {fields.map((f) => (
              <div key={f.key as string}>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`t-${f.key as string}`}>
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                </label>
                {f.type === 'textarea' ? (
                  <textarea
                    id={`t-${f.key as string}`}
                    rows={f.rows ?? 3}
                    value={(draft[f.key] as string) ?? ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="input"
                  />
                ) : (
                  <input
                    id={`t-${f.key as string}`}
                    type="text"
                    value={(draft[f.key] as string) ?? ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="input"
                  />
                )}
              </div>
            ))}

            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => saveLang(active)}
                disabled={busyLang === active}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {busyLang === active ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save {CONTENT_LANGUAGE_NAMES[active]} translation
              </button>
              {present.has(active) && (
                <button
                  type="button"
                  onClick={() => removeLang(active)}
                  disabled={busyLang === active}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {busyLang === active ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              )}
              <button type="button" onClick={onClose} className="ml-auto text-sm text-gray-500 hover:text-gray-700">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TranslationEditorModal